'use strict';

/**
 * Service for lazy loading external JavaScript files at runtime.
 * Used primarily for loading Keycloak.js dynamically when needed.
 *
 * @module services.lazyLoadScript
 */
angular.module('services.lazyLoadScript', [])

.factory('lazyLoadScriptService', ['$q', '$document', function ($q, $document) {
    var service = {};
    var loadedScripts = {}; // Track loaded scripts to prevent duplicates

    /**
     * Loads an external JavaScript file dynamically.
     * Returns a promise that resolves when the script is loaded.
     * If the script was already loaded, returns an already-resolved promise.
     *
     * @param {string} url - The URL of the script to load
     * @returns {Promise} Promise that resolves when script is loaded, rejects on error
     *
     * @example
     * lazyLoadScriptService.loadScript('/assets/scripts/keycloak.js')
     *   .then(function() {
     *     console.log('Script loaded successfully');
     *     // Script is now available (e.g., window.Keycloak is defined)
     *   })
     *   .catch(function(error) {
     *     console.error('Failed to load script:', error);
     *   });
     */
    service.loadScript = function(url) {
        var deferred = $q.defer();

        // Check if script was already loaded (in-memory cache)
        if (loadedScripts[url]) {
            deferred.resolve();
            return deferred.promise;
        }

        // Check if script already exists in DOM (handles page reloads)
        // This prevents duplicate script tags when the cache is reset but DOM persists
        var existingScripts = $document[0].getElementsByTagName('script');
        for (var i = 0; i < existingScripts.length; i++) {
            if (existingScripts[i].src && existingScripts[i].src.indexOf(url) !== -1) {
                // Mark as loaded in cache for future calls
                loadedScripts[url] = true;
                deferred.resolve();
                return deferred.promise;
            }
        }

        // Create script element
        var script = $document[0].createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.async = true;

        // Handle successful load
        script.onload = function() {
            loadedScripts[url] = true;
            deferred.resolve();
        };

        // Handle load error
        script.onerror = function(error) {
            console.error('[lazyLoadScriptService] Failed to load script:', url, error);
            deferred.reject(new Error('Failed to load script: ' + url));
        };

        // Append script to document head
        $document[0].head.appendChild(script);

        return deferred.promise;
    };

    /**
     * Check if a script has already been loaded.
     *
     * @param {string} url - The URL of the script to check
     * @returns {boolean} True if script was loaded, false otherwise
     */
    service.isScriptLoaded = function(url) {
        return !!loadedScripts[url];
    };

    return service;
}]);
